import type { BadgeFlavour } from '@/modules/simulations/types';

// `replaced` is system-managed (row superseded by a newer improvement) and
// hidden from list reads by default — never shown or set by the UI.
export type ImprovementStatus =
  'pending' | 'approved' | 'rejected' | 'partially_addressed' | 'replaced' | 'resolved';

export type ImprovementSeverity = 'high' | 'medium' | 'low';

export interface Improvement {
  readonly improvement_id: string;
  readonly brain_id: string;
  readonly account_id: string;
  status: ImprovementStatus;
  severity: ImprovementSeverity;
  /** Free-form category emitted by the Omni worker, e.g. 'add_knowledge'. */
  improvement_type: string;
  title: string;
  summary: string;
  content: string;
  reasoning: string;
  impact: Record<string, unknown>;
  metadata: Record<string, unknown>;
  feedback: string | null;
  replaced_by: string | null;
  created: string;
  updated: string;
}

export interface ImprovementsListResponse {
  improvements: Improvement[];
  pagination: { limit: number; has_more: boolean; next_cursor: string | null };
}

/**
 * Review actions → the status they submit (D3). Approving is a signal for the
 * Omni worker, not a brain edit; the UI only ever changes the status.
 */
export const REVIEW_ACTION_STATUS = {
  apply: 'approved',
  dismiss: 'rejected',
  archive: 'resolved',
} as const satisfies Record<string, ImprovementStatus>;

export type ReviewAction = keyof typeof REVIEW_ACTION_STATUS;

/** The statuses a review action can submit — the only PATCHes the UI sends. */
export type ReviewStatus = (typeof REVIEW_ACTION_STATUS)[ReviewAction];

export interface UpdateImprovementBody {
  status: ReviewStatus;
  feedback?: string;
}

/** Statuses selectable in the list filter (replaced is system-only, hidden). */
export const FILTERABLE_STATUSES: ImprovementStatus[] = [
  'pending',
  'approved',
  'rejected',
  'partially_addressed',
  'resolved',
];

export const STATUS_TRANSLATION_KEYS: Record<ImprovementStatus, string> = {
  pending: 'improvements.status_pending',
  approved: 'improvements.status_approved',
  rejected: 'improvements.status_rejected',
  partially_addressed: 'improvements.status_partially_addressed',
  replaced: 'improvements.status_replaced',
  resolved: 'improvements.status_resolved',
};

export const STATUS_BADGE_FLAVOUR: Record<ImprovementStatus, BadgeFlavour> = {
  pending: 'informational',
  approved: 'successful',
  rejected: 'destructive',
  partially_addressed: 'warning',
  replaced: 'neutral',
  resolved: 'neutral',
};

export const SEVERITY_TRANSLATION_KEYS: Record<ImprovementSeverity, string> = {
  high: 'improvements.severity_high',
  medium: 'improvements.severity_medium',
  low: 'improvements.severity_low',
};

/** Badge flavour for each severity. */
export const SEVERITY_BADGE_FLAVOUR: Record<ImprovementSeverity, BadgeFlavour> = {
  high: 'destructive',
  medium: 'warning',
  low: 'informational',
};
